import { DateTime, Duration } from "luxon";

import { bootstrapWorkspaceMembershipTags, bootstrapWorkspaceTags } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { BootstrapEventsRegistry } from "@/models/BootstrapEventsRegistry";
import { bulkUpsertTag, replaceAllTags } from "@/models/repositories/Tag";
import {
  bulkUpsertWorkspaceMembershipTag,
  replaceAllWorkspaceMembershipTags,
} from "@/models/repositories/WorkspaceMembershipTag";
import { store } from "@/store/store";

const log = createLogger("bootstrap");

export const POLL_INTERVAL_MS = Duration.fromObject({ seconds: 30 }).toMillis();
export const POLL_JITTER_MS = Duration.fromObject({ seconds: 3 }).toMillis();
const PRUNE_INTERVAL = Duration.fromObject({ minutes: 1 });
export const FULL_SWEEP_INTERVAL_MS = Duration.fromObject({ hours: 24 }).toMillis();
/**
 * Rows per request. The bootstrap endpoints default an *omitted* `pageSize` to 1,000 (SVS-3463), so
 * a request that does not page is silently truncated there; sending it explicitly is what keeps the
 * walk honest. Matches apiv2 `BOOTSTRAP_MAX_PAGE_SIZE` — the ceiling the endpoint clamps to, and
 * already what the cold bootstrap asks of these same routes — so a typical fleet's sweep is one
 * request rather than ten sequential round-trips while the user waits on stale tag columns.
 */
export const TAG_PAGE_SIZE = 10_000;
/**
 * Circuit breaker: 50 pages × 10,000/page = 500k rows, far above the ~10k the largest fleets carry,
 * so a real workspace never trips it — a walk that does is a server returning full pages
 * indefinitely, not a big customer.
 */
export const TAG_MAX_PAGES = 50;
/**
 * Wound back from the request start when advancing a cursor, so a client clock running ahead of
 * the server can't skip rows written inside the skew window. The overlap re-upserts idempotently.
 */
export const CURSOR_SKEW_BUFFER_MS = Duration.fromObject({ minutes: 1 }).toMillis();

/**
 * Minimum age of the last poll before regaining visibility triggers a catch-up. Hidden tabs skip
 * scheduled ticks, so a tag edited in another tab (the Samsara console, typically) would otherwise
 * stay stale until the next tick after the user is already looking at it. Gating on age rather than
 * on whether a tick was skipped covers the common case of a tab hidden for less than one interval,
 * while still collapsing rapid tab churn to one poll. Keep this below `POLL_INTERVAL_MS`: at or
 * above it, a focused tab's own cadence already refreshes sooner and the gate never fires.
 */
export const VISIBILITY_REFRESH_STALENESS_MS = Duration.fromObject({ seconds: 10 }).toMillis();

/**
 * Next poll delay: the base interval ±jitter, so tab herds that loaded together (fleet dispatch
 * floors open dozens of tabs at shift start) desynchronize instead of polling in lockstep. Keep the
 * jitter a fraction of the interval — at ±interval it can schedule a ~0ms delay and busy-loop.
 *
 * Integration-driven tag edits reach the client only through this poll: the server-side AppSync
 * publish for tags is commented out (`TagPopulator`, SVS-2514), so there is no real-time push to
 * fall back on. Deletions ride the same path — they are soft deletes, and the delta endpoint
 * filters on `updatedAt` without an `enabled` predicate, so a tombstone arrives in the next tick
 * and the interval bounds how long a removed tag stays on screen. A quiet tick is two cursor-scoped
 * requests that return nothing; a busy one adds a request per extra page of delta.
 */
export function nextPollDelayMs(random: () => number = Math.random): number {
  return POLL_INTERVAL_MS + Math.round((random() * 2 - 1) * POLL_JITTER_MS);
}

const TAGS_INCREMENTAL_CURSOR = "tagsIncrementalCursor";
const TAGS_FULL_SWEEP = "tagsFullSweep";
const MEMBERSHIP_TAGS_INCREMENTAL_CURSOR = "membershipTagsIncrementalCursor";
const MEMBERSHIP_TAGS_FULL_SWEEP = "membershipTagsFullSweep";

/**
 * Read/write side of the persistent cursor + last-full-sweep timestamp store. Production code
 * passes through to {@link BootstrapEventsRegistry} (IndexedDB-backed); tests inject an in-memory
 * stub so they don't need a real IndexedDB.
 */
export type CursorStore = {
  getEventDate: (event: string, workspaceId: string) => Promise<Date | undefined>;
  setEventDate: (event: string, workspaceId: string, date: Date) => Promise<void>;
};

/** One page of a target's window, as the page walk consumes it. */
export type TagPage = {
  /** The rows this page carried. */
  records: readonly unknown[];
  /**
   * The page size the server actually served. Bootstrap handlers clamp `pageSize` per route, and
   * reading a clamped full page as a short one would end the walk early — truncating the window
   * that the walk exists to fetch whole. Omitted means "the size we asked for".
   */
  pageSize?: number;
};

export type TagSyncTarget = {
  /** Stable name used for the incremental cursor key in the cursor store. */
  cursorEvent: string;
  /** Stable name used for the last-full-sweep timestamp key in the cursor store. */
  fullSweepEvent: string;
  /**
   * Issues one page of the network request. `timestamp` is undefined for full sweeps; the most
   * recent cursor for incrementals.
   */
  fetchPage: (request: { workspaceId: string; page: number; pageSize: number; timestamp?: string }) => Promise<TagPage>;
  /** Merge a delta into the local DB via the existing bulk-upsert path. */
  applyIncremental: (records: readonly unknown[]) => Promise<void>;
  /** Replace the local table contents with a fresh server snapshot. */
  applyFull: (records: readonly unknown[]) => Promise<void>;
};

export type TagPollerDeps = {
  store: CursorStore;
  targets: readonly TagSyncTarget[];
  now: () => Date;
  fullSweepIntervalMs: number;
  /** Rows per request; defaults to {@link TAG_PAGE_SIZE}. Tests shrink it to keep fixtures small. */
  pageSize?: number;
  /** Page-walk circuit breaker; defaults to {@link TAG_MAX_PAGES}. */
  maxPages?: number;
  /**
   * When this returns false, the poll was superseded (e.g. workspace switch) and must not apply
   * results or advance cursors — full sweeps use global replace helpers that would corrupt the
   * active workspace's local DB if a stale response were applied.
   */
  isPollStillActive?: (workspaceId: string) => boolean;
};

const registryStore: CursorStore = {
  getEventDate: (event, workspaceId) => BootstrapEventsRegistry.getInstance().getLatestEventDate(event, workspaceId),
  setEventDate: async (event, workspaceId, date) => {
    await BootstrapEventsRegistry.getInstance().markBootstrap(event, undefined, workspaceId, date);
  },
};

const tagsTarget: TagSyncTarget = {
  cursorEvent: TAGS_INCREMENTAL_CURSOR,
  fullSweepEvent: TAGS_FULL_SWEEP,
  fetchPage: async ({ workspaceId, page, pageSize, timestamp }) => {
    const response = await bootstrapWorkspaceTags(transport, { workspaceId, page, pageSize, timestamp });
    return { records: response?.tags ?? [], pageSize: response?.pageSize };
  },
  applyIncremental: async (records) => {
    await bulkUpsertTag(records as Parameters<typeof bulkUpsertTag>[0]);
  },
  applyFull: async (records) => {
    await replaceAllTags(records as Parameters<typeof replaceAllTags>[0]);
  },
};

const membershipTagsTarget: TagSyncTarget = {
  cursorEvent: MEMBERSHIP_TAGS_INCREMENTAL_CURSOR,
  fullSweepEvent: MEMBERSHIP_TAGS_FULL_SWEEP,
  fetchPage: async ({ workspaceId, page, pageSize, timestamp }) => {
    const response = await bootstrapWorkspaceMembershipTags(transport, { workspaceId, page, pageSize, timestamp });
    return { records: response?.workspaceMembershipTags ?? [], pageSize: response?.pageSize };
  },
  applyIncremental: async (records) => {
    await bulkUpsertWorkspaceMembershipTag(records as Parameters<typeof bulkUpsertWorkspaceMembershipTag>[0]);
  },
  applyFull: async (records) => {
    await replaceAllWorkspaceMembershipTags(records as Parameters<typeof replaceAllWorkspaceMembershipTags>[0]);
  },
};

const productionDeps: TagPollerDeps = {
  store: registryStore,
  targets: [tagsTarget, membershipTagsTarget],
  now: () => new Date(),
  fullSweepIntervalMs: FULL_SWEEP_INTERVAL_MS,
  isPollStillActive: (workspaceId) => activeWorkspaceId === workspaceId,
};

let activeWorkspaceId: string | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let prunerTimer: ReturnType<typeof setInterval> | null = null;
let visibilityListener: (() => void) | null = null;
let lastPollAt: DateTime | null = null;

function isTabHidden(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

/**
 * Starts a poll and stamps it, establishing the freshness basis for the visibility catch-up. The
 * tick is fire-and-forget, so it carries a terminal handler: `syncTarget` guards the failures we
 * know of, and anything that ever gets past them would surface as an unhandled rejection every
 * ~30s for as long as the tab stays visible rather than as one log line (WEB-2245).
 */
function runPoll(workspaceId: string): void {
  lastPollAt = DateTime.fromJSDate(productionDeps.now());
  pollOnce(workspaceId, productionDeps).catch((error) => log.warn("Poll tick failed", { workspaceId, error }));
}

/**
 * Recursive-timeout loop (rather than setInterval) so each tick gets fresh jitter. Hidden tabs
 * skip the network hit entirely — ~87% of sampled fleet sessions are hidden-tab time — and catch
 * up on the next visibility change, subject to `VISIBILITY_REFRESH_STALENESS_MS`.
 */
function scheduleNextPoll(): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = setTimeout(() => {
    pollTimer = null;
    if (!activeWorkspaceId) return;
    if (!isTabHidden()) runPoll(activeWorkspaceId);
    scheduleNextPoll();
  }, nextPollDelayMs());
}

function registerVisibilityListener(): void {
  if (typeof document === "undefined" || visibilityListener) return;
  visibilityListener = () => {
    if (document.visibilityState !== "visible" || !activeWorkspaceId) return;
    const sinceLastPoll =
      lastPollAt === null
        ? Number.POSITIVE_INFINITY
        : DateTime.fromJSDate(productionDeps.now()).diff(lastPollAt).toMillis();
    if (sinceLastPoll < VISIBILITY_REFRESH_STALENESS_MS) return;
    runPoll(activeWorkspaceId);
    // Restart the loop from the catch-up poll so a scheduled tick landing right after it
    // doesn't double-fire.
    scheduleNextPoll();
  };
  document.addEventListener("visibilitychange", visibilityListener);
}

/**
 * Stops any in-progress polling for the previous workspace and starts polling tag/membership-tag
 * changes for `workspaceId`. The first tick runs immediately — a full sweep is now a set of
 * in-memory upserts, so there is no boot-write contention to defer around.
 */
export function startTagPolling(workspaceId: string): void {
  if (!workspaceId) return;
  if (activeWorkspaceId === workspaceId && pollTimer) return;

  stopTagPolling();
  activeWorkspaceId = workspaceId;

  log.info("Starting tag polling", { workspaceId });

  runPoll(workspaceId);
  registerVisibilityListener();
  scheduleNextPoll();

  startPruner();
}

/**
 * Stops the tag poller. Safe to call repeatedly. The pruner stays running because deleting
 * tombstoned membership-tag rows is a workspace-agnostic local-DB cleanup and incurs no network
 * cost — keeping it ticking avoids resurrecting it on every workspace switch.
 */
export function stopTagPolling(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  activeWorkspaceId = null;
  lastPollAt = null;
}

function startPruner(): void {
  if (prunerTimer) return;
  prunerTimer = setInterval(() => {
    try {
      store.deleteWhere("workspaceMembershipTag", (tag) => !tag.enabled);
    } catch (error) {
      log.warn("Pruner failed to delete disabled membership tags", { error });
    }
  }, PRUNE_INTERVAL.toMillis());
}

/**
 * Runs a single poll tick across every configured tag target. Exported so tests can drive the
 * poller deterministically without spinning up real timers, and so callers can force-refresh on
 * demand if a future feature ever needs that hook.
 */
export async function pollOnce(workspaceId: string, deps: TagPollerDeps = productionDeps): Promise<void> {
  await Promise.all(deps.targets.map((target) => syncTarget(workspaceId, target, deps)));
}

function pollWorkspaceStillActive(deps: TagPollerDeps, workspaceId: string): boolean {
  return deps.isPollStillActive?.(workspaceId) ?? true;
}

/**
 * Reads a cursor, treating an unreadable store as "no cursor". These reads sit ahead of each
 * target's own guard, so a throw would abort the whole tick — discarding the sibling target's
 * work along with this one's. Falling back to no cursor degrades to a full sweep, which is what
 * every other first-poll path already does (WEB-2245).
 */
async function readCursor(event: string, workspaceId: string, deps: TagPollerDeps): Promise<Date | undefined> {
  try {
    return await deps.store.getEventDate(event, workspaceId);
  } catch (error) {
    log.warn("Cursor read failed", { workspaceId, event, error });
    return undefined;
  }
}

async function syncTarget(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  const lastFullSweep = await readCursor(target.fullSweepEvent, workspaceId, deps);
  const fullSweepDue =
    !lastFullSweep ||
    DateTime.fromJSDate(deps.now()).diff(DateTime.fromJSDate(lastFullSweep)).toMillis() >= deps.fullSweepIntervalMs;

  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  if (fullSweepDue) {
    await runFullSweep(workspaceId, target, deps);
    return;
  }

  await runIncremental(workspaceId, target, deps);
}

/**
 * Walks a target's window one page at a time, handing each page to `onPage` as it lands.
 *
 * Returns false when the walk did not reach the end of the window — the poll was superseded, or
 * the circuit breaker tripped. Callers must then leave their cursors where they are, so the next
 * poll retries the same window instead of filtering the unfetched rows out of every future one.
 */
async function walkPages(
  workspaceId: string,
  target: TagSyncTarget,
  deps: TagPollerDeps,
  timestamp: string | undefined,
  onPage: (records: readonly unknown[]) => Promise<void>,
): Promise<boolean> {
  const requestedPageSize = deps.pageSize ?? TAG_PAGE_SIZE;
  const maxPages = deps.maxPages ?? TAG_MAX_PAGES;

  for (let page = 0; page < maxPages; page++) {
    if (!pollWorkspaceStillActive(deps, workspaceId)) return false;

    const { records, pageSize = requestedPageSize } = await target.fetchPage({
      workspaceId,
      page,
      pageSize: requestedPageSize,
      timestamp,
    });
    if (!pollWorkspaceStillActive(deps, workspaceId)) return false;

    await onPage(records);

    if (records.length < Math.min(pageSize, requestedPageSize)) return true;
  }

  log.warn("Hit max page limit; stopping pagination", {
    workspaceId,
    event: target.cursorEvent,
    maxPages,
    pageSize: requestedPageSize,
  });
  return false;
}

async function runFullSweep(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  const sweptAt = deps.now();
  try {
    // Buffer the whole window before applying it: `applyFull` clears the table and refills it, so
    // handing it a partial walk blanks every row the walk never reached (WEB-2253).
    const records: unknown[] = [];
    const walkedWholeWindow = await walkPages(workspaceId, target, deps, undefined, async (page) => {
      records.push(...page);
    });
    if (!walkedWholeWindow) return;
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    await target.applyFull(records);
    await deps.store.setEventDate(target.fullSweepEvent, workspaceId, sweptAt);
    await deps.store.setEventDate(target.cursorEvent, workspaceId, skewBufferedCursor(sweptAt));
    log.info("Full sweep complete", {
      workspaceId,
      event: target.fullSweepEvent,
      count: records.length,
      sweptAt: sweptAt.toISOString(),
    });
  } catch (error) {
    log.warn("Full sweep failed", { workspaceId, event: target.fullSweepEvent, error });
  }
}

function skewBufferedCursor(requestStart: Date): Date {
  return DateTime.fromJSDate(requestStart).minus({ milliseconds: CURSOR_SKEW_BUFFER_MS }).toJSDate();
}

async function runIncremental(workspaceId: string, target: TagSyncTarget, deps: TagPollerDeps): Promise<void> {
  const cursorDate = await readCursor(target.cursorEvent, workspaceId, deps);
  if (!pollWorkspaceStillActive(deps, workspaceId)) return;

  const sentTimestamp = cursorDate?.toISOString();
  const requestStart = deps.now();

  try {
    // Applied per page rather than buffered: the incremental path is purely additive, so a page
    // is useful the moment it lands and nothing downstream needs the window whole.
    let count = 0;
    const walkedWholeWindow = await walkPages(workspaceId, target, deps, sentTimestamp, async (page) => {
      count += page.length;
      if (page.length > 0) {
        await target.applyIncremental(page);
      }
    });
    if (!walkedWholeWindow) return;
    if (!pollWorkspaceStillActive(deps, workspaceId)) return;

    const nextCursor = skewBufferedCursor(requestStart);
    await deps.store.setEventDate(target.cursorEvent, workspaceId, nextCursor);
    log.info("Incremental sync complete", {
      workspaceId,
      event: target.cursorEvent,
      count,
      sentTimestamp,
      nextCursor: nextCursor.toISOString(),
    });
  } catch (error) {
    log.warn("Incremental sync failed", { workspaceId, event: target.cursorEvent, error });
  }
}

/**
 * Test-only reset hook. Clears in-memory state between tests so each test starts from a clean
 * slate; production code never resets these fields directly.
 */
export function __resetTagPollerForTests(): void {
  stopTagPolling();
  if (prunerTimer) {
    clearInterval(prunerTimer);
    prunerTimer = null;
  }
  if (visibilityListener && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", visibilityListener);
  }
  visibilityListener = null;
}
